import { EMR_PAGE_SOURCE_LABELS } from "@emr/contracts";
import { type ReactNode, useId, useMemo } from "react";
import {
  CHART_SECTION_LABELS,
  CHART_SECTIONS,
  type ChartSection,
  type ExtractedChartPage,
  type ReviewItem,
  reviewItems,
} from "./extraction";
import "./emr-import.css";

function capturedTime(capturedAt: string): string {
  const at = new Date(capturedAt);
  return Number.isNaN(at.getTime())
    ? "just now"
    : `at ${at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

/** Above the filled-in form: where it came from, and that nothing is saved yet. */
export function EmrImportBanner({
  page,
  capturedAt,
  onDiscard,
  disabled,
}: {
  readonly page: ExtractedChartPage;
  readonly capturedAt: string;
  readonly onDiscard: () => void;
  readonly disabled?: boolean;
}) {
  return (
    <div className="pp-import-banner" role="status">
      <p>
        <strong>Filled from {EMR_PAGE_SOURCE_LABELS[page.source]}</strong>{" "}
        {capturedTime(capturedAt)}. Check every field and entry below; nothing is saved until you
        register.
      </p>
      <button type="button" className="pp-btn ghost sm" onClick={onDiscard} disabled={disabled}>
        Discard import
      </button>
    </div>
  );
}

/** Every chart entry the page held, each kept unless the physician unticks it. */
export function EmrImportChartReview({
  page,
  kept,
  onKeptChange,
  extra,
  disabled,
}: {
  readonly page: ExtractedChartPage;
  readonly kept: ReadonlySet<string>;
  readonly onKeptChange: (next: ReadonlySet<string>) => void;
  /** Further entries to keep or leave out, shown after the chart sections. */
  readonly extra?: ReactNode;
  readonly disabled?: boolean;
}) {
  const label = EMR_PAGE_SOURCE_LABELS[page.source];
  const items = useMemo(() => reviewItems(page), [page]);
  const groups = CHART_SECTIONS.map(
    (section) => [section, items.filter((item) => item.section === section)] as const,
  ).filter(([, entries]) => entries.length > 0);

  const setKept = (keys: readonly string[], keep: boolean) => {
    const next = new Set(kept);
    for (const key of keys) {
      if (keep) next.add(key);
      else next.delete(key);
    }
    onKeptChange(next);
  };

  return (
    <fieldset className="pp-import-chart" disabled={disabled}>
      <legend className="pp-subhead">Chart entries from {label}</legend>
      <p className="pp-quiet">
        Untick anything that should not go on the new chart. Kept entries are marked Imported.
      </p>
      {groups.length === 0 ? (
        <p className="pp-quiet">
          The page showed no medications, allergies, history, immunizations or results.
        </p>
      ) : (
        groups.map(([section, entries]) => (
          <ReviewGroup
            key={section}
            section={section}
            entries={entries}
            kept={kept}
            onChange={setKept}
          />
        ))
      )}
      {extra}
    </fieldset>
  );
}

function ReviewGroup({
  section,
  entries,
  kept,
  onChange,
}: {
  readonly section: ChartSection;
  readonly entries: readonly ReviewItem[];
  readonly kept: ReadonlySet<string>;
  readonly onChange: (keys: readonly string[], keep: boolean) => void;
}) {
  const keys = entries.map((entry) => entry.key);
  const keptHere = keys.filter((key) => kept.has(key)).length;
  return (
    <div className="pp-import-group">
      <label className="pp-import-group-head">
        <input
          type="checkbox"
          checked={keptHere === keys.length}
          ref={(input) => {
            if (input) input.indeterminate = keptHere > 0 && keptHere < keys.length;
          }}
          onChange={(event) => onChange(keys, event.target.checked)}
        />
        <span>{CHART_SECTION_LABELS[section]}</span>
        <span className="pp-quiet">
          {keptHere} of {keys.length} kept
        </span>
      </label>
      <ul className="pp-import-items">
        {entries.map((entry) => (
          <li key={entry.key}>
            <label className="pp-import-item">
              <input
                type="checkbox"
                checked={kept.has(entry.key)}
                onChange={(event) => onChange([entry.key], event.target.checked)}
              />
              <span className="pp-import-item-text">
                <span className="pp-import-item-title">{entry.title}</span>
                {entry.detail ? <span className="pp-quiet">{entry.detail}</span> : null}
              </span>
              {entry.status ? <span className="pp-chip quiet">{entry.status}</span> : null}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What the page showed that BobDoc has no place for: shown, never saved. */
export function EmrImportLeftOut({ page }: { readonly page: ExtractedChartPage }) {
  const titleId = useId();
  if (page.notImported.length === 0) return null;
  return (
    <section className="pp-import-left-out" aria-labelledby={titleId}>
      <p className="pp-subhead" id={titleId}>
        Not imported
      </p>
      <p className="pp-quiet">
        BobDoc has no place for these. Re-enter anything important on the chart after you register.
      </p>
      <dl className="pp-import-left-out-list">
        {page.notImported.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the list is fixed for one page and two rows can read the same.
          <div key={`${index}:${item.section}:${item.label}`}>
            <dt>
              {item.label === item.section ? item.section : `${item.section} · ${item.label}`}
            </dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
